import React, { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import IconSearch from 'src/assets/images/icons/search-settings.svg';
import debounce from 'lodash/debounce';
import { useColumnHeaderContext } from '../hooks/useColumnHeaderContext';
import { SortButton } from './SortButton';

export const ColumnHeaderMenuTop: React.FC = () => {
  const { filter, search, items } = useColumnHeaderContext();
  const [searchTerm, setSearchTerm] = useState(search?.term || '');

  // Debounce the search term update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      search?.onChange(value);
    }, 500),
    [search]
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    search?.onChange('');
    debouncedSearch.cancel();
  };

  // Sync local state with context when context changes externally
  useEffect(() => {
    if (search?.term !== undefined && search.term !== searchTerm) {
      // Only update if the difference is significant or it's a clear action from outside
      // But typically we trust local state for immediate feedback.
      // If the context updates (e.g. clear all filters), we should respect it.
      if (search.term === '') {
        setSearchTerm('');
      }
    }
    // We only want to listen to search.term changes that might come from other places
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search?.term]);

  return (
    <>
      {/* Sort Buttons */}
      <SortButton direction='asc' />
      <SortButton direction='desc' />

      {(items?.items?.length > 0 || (search?.term !== '' && items?.items?.length === 0)) && (
        <>
          {/* Filter Header: Select All / Clear */}
          <div className='flex items-center justify-between mt-8 px-8 py-16 border-t border-neutral-20'>
            <div className='flex items-center gap-8'>
              <p className='text-12 lg:text-14 font-medium'>Filter Value to Display</p>
            </div>
            <div className='flex items-center gap-10'>
              <button type='button' onClick={filter.onSelectAll} className='text-10 xl:text-12 font-medium text-primary'>
                Select All
              </button>
              <div className='w-1 h-12 bg-neutral-20' />
              <button type='button' onClick={filter.onClearAll} className='text-10 xl:text-12 font-medium text-neutral-60'>
                Clear
              </button>
            </div>
          </div>

          {/* Search Input */}
          <div className='pt-4 pb-16 px-8 border-b border-neutral-20'>
            <div className='relative'>
              <img src={IconSearch} alt='search' className='absolute left-16 top-1/2 -translate-y-1/2' />
              <input
                type='text'
                placeholder='Search'
                className='w-full pl-48 pr-16 py-12 bg-neutral-10 border border-neutral-20 rounded-8'
                value={searchTerm}
                onChange={handleInputChange}
              />
              <button
                type='button'
                className='absolute right-16 top-1/2 -translate-y-1/2 text-neutral-60'
                onClick={handleClearSearch}
                aria-label='Clear Search'
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
